import { useCallback, useEffect, useState } from "react";
import { publicApi } from "@/lib/api";
import { TID } from "@/lib/constants";
import { Search as SearchIcon } from "lucide-react";
import FlipCard from "@/components/FlipCard";

export default function SearchPage() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [chips, setChips] = useState(["COP30", "Finance Commission", "Haryana Budget"]);
  const [loading, setLoading] = useState(false);
  const [ran, setRan] = useState(false);

  useEffect(() => {
    publicApi.trending()
      .then((d) => setChips(d.chips && d.chips.length ? d.chips : chips))
      .catch((err) => { console.warn("[search] trending fetch failed:", err?.message || err); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = useCallback(async (query) => {
    setLoading(true);
    setRan(true);
    try {
      const d = await publicApi.searchCards(query);
      setResults(d.cards || []);
    } catch (err) {
      console.error("[search] failed:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    runSearch(q.trim());
  };

  const onChip = (c) => {
    setQ(c);
    runSearch(c);
  };

  return (
    <div className="min-h-screen bg-[#111622]">
      <div
        className="mx-auto w-full max-w-[760px] px-4 sm:px-6 pt-5"
        style={{ paddingBottom: "calc(88px + env(safe-area-inset-bottom))" }}
      >
        <h1 className="text-lg font-semibold text-[#F8FAFC]">Search</h1>
        <p className="mt-1 text-sm text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
          Keywords, syllabus tags, or state — all in one line.
        </p>

        <form onSubmit={onSubmit} className="mt-4 relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" aria-hidden />
          <input
            data-testid={TID.searchInput}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. Finance Commission, #GS3, #HPSC…"
            className="h-12 w-full rounded-xl border border-[#26334D] bg-[#1A2232] pl-9 pr-4 text-sm text-[#F8FAFC] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
            aria-label="Search"
          />
        </form>

        <div className="mt-3 flex flex-wrap gap-2">
          <span className="text-xs text-[#94A3B8] mr-1 self-center">Trending:</span>
          {chips.map((c) => (
            <button
              key={c}
              onClick={() => onChip(c)}
              data-testid={TID.searchTrendingChip}
              className="rounded-full border border-[#26334D] bg-[#1A2232] px-3 py-1 text-xs font-semibold text-[#F8FAFC] hover:bg-[#232E45] transition-colors"
            >{c}</button>
          ))}
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="space-y-4">
              {[0, 1, 2].map((k) => (
                <div key={k} className="h-72 rounded-2xl bg-[#1A2232] border border-[#26334D] animate-pulse" />
              ))}
            </div>
          ) : results.length === 0 ? (
            ran ? (
              <div className="rounded-2xl border border-[#26334D] bg-[#1A2232] p-6 text-center">
                <p className="text-sm font-semibold text-[#F8FAFC]">No results.</p>
                <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>Try a broader keyword or tap a trending chip.</p>
              </div>
            ) : null
          ) : (
            <ul className="space-y-5">
              {results.map((c) => (
                <li
                  key={c.card_id}
                  data-testid={TID.searchResult}
                  className="space-y-2"
                >
                  {/* Interactive FlipCard — same experience as the main feed.
                      Users can tap Flip to read Prelims Pointers, Diagram,
                      Mains Utility, and Full Brief inline. */}
                  <div
                    className="relative w-full mx-auto"
                    style={{ maxWidth: 720, height: "min(72dvh, 620px)", perspective: 1200 }}
                  >
                    <FlipCard
                      card={c}
                      isBookmarked={false}
                      onToggleBookmark={() => {
                        // Search view doesn't own bookmarking state; nudge readers
                        // that this action lives on the main feed / saved page.
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
